import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { Button, Form, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { ChevronLeft, ChevronRight, Lock, Pencil, Plus, Settings, Trash2, Unlock, X } from "lucide-react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import Loader from "../components/common/Loader";
import {
    createTradingBet,
    createTradingPair,
    deleteBetScreenshot,
    deleteTradingBet,
    deleteTradingPair,
    getTradingData,
    updateTradingBaseAmount,
    updateTradingBet,
    updateTradingDeposit,
    uploadBetScreenshot,
} from "../api/trading";

dayjs.extend(isoWeek);

const MAX_PENDING_BETS = 2;

const STATUS_COLOR = {
    pending: "#0d6efd",
    take: "#198754",
    stop: "#dc3545",
    cancelled: "#ffffff",
};

// Клік по кружку перебирає статуси по колу: очікує → тейк → стоп → скасовано → знову очікує.
const STATUS_CYCLE = ["pending", "take", "stop", "cancelled"];

const DEFAULT_ECONOMICS = {
    amountMode: "usdt",
    direction: "long",
    amount: "5",
    leverage: "50",
    takePercent: "2",
    stopPercent: "2",
    rr: "1",
};

const DIRECTION_LETTER = { long: "L", short: "S" };

const nowForInput = () => dayjs().format("YYYY-MM-DDTHH:mm");
const toInputValue = (date) => (date ? dayjs(date).format("YYYY-MM-DDTHH:mm") : nowForInput());
const formatDateTime = (date) => (date ? dayjs(date).format("D MMM YYYY HH:mm") : "");
const formatAmount = (bet) => (bet.amountMode === "usdt" ? `$${bet.amount}` : `${bet.amount}%`);

const toNum = (v) => {
    if (v === "" || v === null || v === undefined) return NaN;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
};
const round2 = (n) => Math.round(n * 100) / 100;

const WHATIF_BASE_PERCENT = 1; // базовий % депозиту для режиму перегляду "якби ставив відсотком"

// Комісії Binance USDT-M Futures, звичайний рівень (без знижки за оплату BNB):
// maker 0.02%, taker 0.05% від номіналу позиції (margin × leverage). Вхід у позицію —
// завжди маркет-ордер (taker), тейк і стоп — лімітні ордери (maker), тому за один
// повний цикл угоди (вхід + вихід) стягується taker + maker з номіналу.
const TAKER_FEE_PERCENT = 0.05;
const MAKER_FEE_PERCENT = 0.02;
const ROUND_TRIP_FEE_PERCENT = TAKER_FEE_PERCENT + MAKER_FEE_PERCENT;

function resolvedBetsChronological(pairs) {
    const resolved = [];
    for (const pair of pairs) {
        for (const bet of pair.bets) {
            if (bet.status === "take" || bet.status === "stop") {
                resolved.push({ ...bet, pairId: pair._id, pairSymbol: pair.symbol });
            }
        }
    }
    resolved.sort((a, b) => new Date(a.resolvedAt) - new Date(b.resolvedAt));
    return resolved;
}

/**
 * Реплей балансу від стартового депозиту через усі закриті ставки (у порядку resolvedAt).
 * Ставки в режимі "% депозиту" рахують маржу від балансу на момент своєї черги в реплеї —
 * тому порядок закриття важливий і не може бути пропущений.
 */
function computeBalanceHistory(deposit, pairs) {
    const resolved = resolvedBetsChronological(pairs);

    let balance = Number(deposit) || 0;
    const points = [{ time: null, balance: round2(balance) }];
    for (const bet of resolved) {
        const amount = Number(bet.amount);
        const leverage = Number(bet.leverage);
        const margin = bet.amountMode === "usdt" ? amount : (balance * amount) / 100;
        const pnlPercent = bet.status === "take" ? Number(bet.percent) : -Number(bet.stopPercent);
        // Захист від "битих" ставок (наприклад створених до появи цих полів) — щоб одна
        // ставка з відсутніми даними не зіпсувала NaN'ом увесь подальший баланс.
        const valid = Number.isFinite(margin) && Number.isFinite(leverage) && Number.isFinite(pnlPercent);
        const pnl = valid ? margin * leverage * (pnlPercent / 100) : 0;
        const fee = valid ? (margin * leverage * ROUND_TRIP_FEE_PERCENT) / 100 : 0;
        balance += pnl - fee;
        points.push({
            time: bet.resolvedAt,
            balance: round2(balance),
            pnl: round2(pnl),
            fee: round2(fee),
            pairSymbol: bet.pairSymbol,
        });
    }
    return { balance: round2(balance), points };
}

/**
 * "А що якби" — той самий реплей, але розмір ставки завжди WHATIF_BASE_PERCENT% від поточного
 * (симульованого) балансу, з тим самим правилом подвоєння після стопу й скиданням до бази
 * після тейку, окремо по кожній парі — фактичний записаний amount/amountMode ігнорується,
 * стадія мартингейлу симулюється заново з нуля по статусах take/stop.
 * Якщо симульований баланс падає до нуля чи нижче — рахунок вважається злитим (ruinedAt),
 * подальші ставки в реплеї більше не мають маржі для ставки.
 */
function computeBalanceHistoryPercent(deposit, pairs, basePercent = WHATIF_BASE_PERCENT) {
    const resolved = resolvedBetsChronological(pairs);

    const stageByPair = {};
    let balance = Number(deposit) || 0;
    let ruinedAt = null;
    const points = [{ time: null, balance: round2(balance) }];
    for (const bet of resolved) {
        const stage = stageByPair[bet.pairId] || 0;
        const sizePercent = basePercent * 2 ** stage;
        const leverage = Number(bet.leverage);
        const pnlPercent = bet.status === "take" ? Number(bet.percent) : -Number(bet.stopPercent);
        const margin = balance > 0 ? (balance * sizePercent) / 100 : 0;
        const valid = Number.isFinite(margin) && Number.isFinite(leverage) && Number.isFinite(pnlPercent);
        const pnl = valid ? margin * leverage * (pnlPercent / 100) : 0;
        const fee = valid ? (margin * leverage * ROUND_TRIP_FEE_PERCENT) / 100 : 0;
        balance += pnl - fee;
        if (balance <= 0 && ruinedAt === null) {
            ruinedAt = bet.resolvedAt;
            balance = 0;
        }
        stageByPair[bet.pairId] = bet.status === "take" ? 0 : stage + 1;
        points.push({
            time: bet.resolvedAt,
            balance: round2(balance),
            pnl: round2(pnl),
            fee: round2(fee),
            pairSymbol: bet.pairSymbol,
            sizePercent,
        });
    }
    return { balance: round2(balance), points, ruinedAt };
}

// Пресети періодів для перегляду журналу. "all" — без нижньої межі (весь час,
// фактично з дати заведення журналу, бо старіших ставок у базі немає).
const PERIODS = [
    { key: "all", label: "Весь час" },
    { key: "today", label: "Сьогодні" },
    { key: "yesterday", label: "Вчора" },
    { key: "7d", label: "7д" },
    { key: "10d", label: "10д" },
    { key: "thisWeek", label: "Цей тиждень" },
    { key: "lastWeek", label: "Минулий тиждень" },
    { key: "custom", label: "Свій період" },
];

// custom — { from, to } рядки з <input type="date">, довільний діапазон обраний вручну.
// end включно до кінця обраного дня (23:59:59.999), інакше ставки, закриті сьогодні, випадали б.
function periodRange(key, custom) {
    const now = dayjs();
    switch (key) {
        case "today":
            return { start: now.startOf("day"), end: now };
        case "yesterday": {
            const yesterday = now.subtract(1, "day");
            return { start: yesterday.startOf("day"), end: yesterday.endOf("day") };
        }
        case "7d":
            return { start: now.subtract(7, "day"), end: now };
        case "10d":
            return { start: now.subtract(10, "day"), end: now };
        case "thisWeek":
            return { start: now.startOf("isoWeek"), end: now };
        case "lastWeek": {
            const lastWeek = now.subtract(1, "week");
            return { start: lastWeek.startOf("isoWeek"), end: lastWeek.endOf("isoWeek") };
        }
        case "custom": {
            const start = custom?.from ? dayjs(custom.from).startOf("day") : null;
            const end = custom?.to ? dayjs(custom.to).endOf("day") : now;
            return { start, end };
        }
        case "all":
        default:
            return { start: null, end: now };
    }
}

// Баланс "фікс"-реплею станом на певний момент — тобто з урахуванням лише тих
// ставок, що закрились (resolvedAt) до цього моменту включно. momentDayjs === null
// означає "з самого початку", тобто баланс дорівнює стартовому депозиту.
function balanceAsOf(fixedPoints, deposit, momentDayjs) {
    if (!momentDayjs) return round2(Number(deposit) || 0);
    let balance = Number(deposit) || 0;
    for (const p of fixedPoints) {
        if (!p.time) continue;
        if (dayjs(p.time).isAfter(momentDayjs)) break;
        balance = p.balance;
    }
    return round2(balance);
}

/**
 * Серії тейків/стопів підряд окремо в межах кожної пари (за resolvedAt), а потім
 * зібрані разом у загальну статистику довжин. Раніше рахувалось у загальному
 * хронологічному порядку по всіх парах разом — через це серія довжиною, скажімо,
 * 12 виявлялась мішаниною з десятка різних інструментів, що просто закрились
 * одне за одним у вузькому вікні часу, а не реальною серією поспіль на одній парі.
 *
 * Приймає вже відфільтрований (за потреби) список закритих ставок у форматі
 * resolvedBetsChronological — тобто з полем pairId і глобально відсортований за
 * resolvedAt. Це дозволяє рахувати серії як за весь час, так і за довільний період.
 */
function computeStreaks(resolvedBets) {
    const byPair = new Map();
    for (const bet of resolvedBets) {
        if (!byPair.has(bet.pairId)) byPair.set(bet.pairId, []);
        byPair.get(bet.pairId).push(bet);
    }

    const winStreaks = [];
    const lossStreaks = [];

    for (const bets of byPair.values()) {
        // bets усередині кожної пари вже в порядку resolvedAt, бо resolvedBets
        // глобально відсортований і ми зберігаємо порядок вставки.
        let currentStatus = null;
        let currentLen = 0;
        for (const bet of bets) {
            if (bet.status === currentStatus) {
                currentLen++;
            } else {
                if (currentStatus === "take") winStreaks.push(currentLen);
                else if (currentStatus === "stop") lossStreaks.push(currentLen);
                currentStatus = bet.status;
                currentLen = 1;
            }
        }
        if (currentStatus === "take") winStreaks.push(currentLen);
        else if (currentStatus === "stop") lossStreaks.push(currentLen);
    }

    return { winStreaks: groupStreakLengths(winStreaks), lossStreaks: groupStreakLengths(lossStreaks) };
}

// [{довжина серії, скільки разів вона траплялась}], відсортовано за довжиною.
function groupStreakLengths(lengths) {
    const counts = {};
    for (const len of lengths) counts[len] = (counts[len] || 0) + 1;
    return Object.keys(counts)
        .map(Number)
        .sort((a, b) => a - b)
        .map((len) => ({ len, count: counts[len] }));
}

// Поточна (ще не перервана тейком) серія стопів підряд, рахуючи з останньої
// закритої ставки назад у часі. Не залежить від pending-ставок — вони ще не
// вплинули на серію.
function pairCurrentLossStreak(pair) {
    const resolved = [...pair.bets]
        .filter((b) => b.status === "take" || b.status === "stop")
        .sort((a, b) => new Date(a.placedAt) - new Date(b.placedAt));
    let streak = 0;
    for (let i = resolved.length - 1; i >= 0; i--) {
        if (resolved[i].status !== "stop") break;
        streak++;
    }
    return streak;
}

/**
 * Кількість відкритих ставок (за placedAt) по календарних тижнях (пн-нд), по всіх парах —
 * незалежно від обраного періоду перегляду журналу, бо це показник активності самого трейдера,
 * а не результатів. Тейк/стоп/вінрейт рахуються по СТАТУСУ ставки на момент виклику, згрупованій
 * за тижнем ВІДКРИТТЯ (placedAt), а не закриття — так тиждень з наочно видно, наскільки вдалими
 * були саме ставки, зроблені того тижня, навіть якщо частина з них ще pending. Тижні без жодної
 * ставки між першою й останньою включаються з count: 0, щоб графік не спотворював розриви в
 * активності відсутніми стовпчиками.
 */
function computeWeeklyActivity(pairs) {
    const stats = new Map();
    for (const pair of pairs) {
        for (const bet of pair.bets) {
            const weekStart = dayjs(bet.placedAt).startOf("isoWeek").format("YYYY-MM-DD");
            const entry = stats.get(weekStart) || { count: 0, take: 0, stop: 0 };
            entry.count++;
            if (bet.status === "take") entry.take++;
            else if (bet.status === "stop") entry.stop++;
            stats.set(weekStart, entry);
        }
    }
    const weeks = [...stats.keys()].sort();
    if (weeks.length === 0) return [];
    const end = dayjs(weeks[weeks.length - 1]);
    const result = [];
    let cur = dayjs(weeks[0]);
    while (!cur.isAfter(end)) {
        const key = cur.format("YYYY-MM-DD");
        const entry = stats.get(key) || { count: 0, take: 0, stop: 0 };
        const resolvedTotal = entry.take + entry.stop;
        result.push({
            weekStart: key,
            label: cur.format("D MMM"),
            count: entry.count,
            take: entry.take,
            stop: entry.stop,
            winRate: resolvedTotal > 0 ? round2((entry.take / resolvedTotal) * 100) : null,
        });
        cur = cur.add(1, "week");
    }
    return result;
}

function pairWinRate(pair) {
    let wins = 0;
    let losses = 0;
    for (const bet of pair.bets) {
        if (bet.status === "take") wins++;
        else if (bet.status === "stop") losses++;
    }
    const total = wins + losses;
    return total > 0 ? round2((wins / total) * 100) : null;
}

function BalanceChart({ points }) {
    if (points.length < 2) {
        return <div className="text-muted small mb-4">Графік з'явиться після першої закритої ставки.</div>;
    }
    const data = points.map((p, i) => ({
        key: i,
        label: p.time ? dayjs(p.time).format("D MMM HH:mm") : "Старт",
        balance: p.balance,
    }));
    return (
        <div className="mb-4" style={{ height: 220 }}>
            <ResponsiveContainer>
                <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6c757d" }} minTickGap={24} />
                    <YAxis
                        tick={{ fontSize: 11, fill: "#6c757d" }}
                        width={64}
                        tickFormatter={(v) => `$${v}`}
                    />
                    <RechartsTooltip formatter={(v) => [`$${v}`, "Баланс"]} />
                    <Line
                        type="monotone"
                        dataKey="balance"
                        stroke="#0d6efd"
                        strokeWidth={2}
                        strokeLinecap="round"
                        dot={false}
                        activeDot={{ r: 4 }}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function WeeklyActivityTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
        <div className="bg-white border rounded shadow-sm p-2" style={{ fontSize: 13 }}>
            <div className="fw-semibold mb-1">Тиждень з {p.label}</div>
            <div>Ставок: <span className="text-primary fw-semibold">{p.count}</span></div>
            <div>
                Тейк / Стоп: <span className="text-success fw-semibold">{p.take}</span>
                {" / "}
                <span className="text-danger fw-semibold">{p.stop}</span>
            </div>
            <div>Winrate: <span className="fw-semibold">{p.winRate == null ? "—" : `${p.winRate}%`}</span></div>
        </div>
    );
}

function WeeklyActivityChart({ data }) {
    if (data.length === 0) {
        return <div className="text-muted small mb-4">Графік з'явиться після першої ставки.</div>;
    }
    return (
        <div className="mb-4">
            <div className="text-muted small mb-1">Активність (ставок на тиждень)</div>
            <div style={{ height: 160 }}>
                <ResponsiveContainer>
                    <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6c757d" }} minTickGap={24} />
                        <YAxis tick={{ fontSize: 11, fill: "#6c757d" }} width={32} allowDecimals={false} />
                        <RechartsTooltip content={<WeeklyActivityTooltip />} />
                        <Bar dataKey="count" fill="#0d6efd" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

/**
 * Розмір ставки (% депозиту / USDT) + плече + зв'язаний калькулятор тейк% / стоп% / RR:
 * коли відомі два з трьох значень, третє довираховується автоматично (останні 2 відредаговані
 * поля вважаються джерелом, найдавніше — виводиться).
 */
const BetEconomicsFields = forwardRef(function BetEconomicsFields({ initial, previousBet, balance }, ref) {
    const [amountMode, setAmountMode] = useState(initial.amountMode);
    const [direction, setDirection] = useState(initial.direction);
    const [amount, setAmount] = useState(initial.amount);
    const [leverage, setLeverage] = useState(initial.leverage);
    const [take, setTake] = useState(initial.takePercent);
    const [stop, setStop] = useState(initial.stopPercent);
    const [rr, setRR] = useState(initial.rr);
    const order = useRef(["take", "stop"]);

    useImperativeHandle(ref, () => ({
        getValues: () => ({
            amountMode,
            direction,
            amount,
            leverage,
            takePercent: take,
            stopPercent: stop,
            rr,
        }),
    }));

    const touch = (field) => {
        order.current = [...order.current.filter((f) => f !== field), field].slice(-2);
    };

    const recompute = (field, value) => {
        const values = { take, stop, rr, [field]: value };
        const computed = ["take", "stop", "rr"].find((f) => !order.current.includes(f));
        const t = toNum(values.take);
        const s = toNum(values.stop);
        const r = toNum(values.rr);
        if (computed === "rr" && Number.isFinite(t) && Number.isFinite(s) && s !== 0) {
            values.rr = String(round2(t / s));
        } else if (computed === "take" && Number.isFinite(r) && Number.isFinite(s)) {
            values.take = String(round2(r * s));
        } else if (computed === "stop" && Number.isFinite(t) && Number.isFinite(r) && r !== 0) {
            values.stop = String(round2(t / r));
        }
        return values;
    };

    const applyChange = (field, value) => {
        touch(field);
        const next = recompute(field, value);
        setTake(next.take);
        setStop(next.stop);
        setRR(next.rr);
    };

    const handleDouble = () => {
        if (!previousBet) return;
        setAmountMode(previousBet.amountMode);
        setAmount(String(Number(previousBet.amount) * 2));
    };

    const roiTarget = Number.isFinite(toNum(take)) && Number.isFinite(toNum(leverage))
        ? round2(toNum(take) * toNum(leverage))
        : null;

    const amountUsdPreview =
        amountMode === "percent" && Number.isFinite(toNum(amount)) && Number.isFinite(toNum(balance))
            ? round2((toNum(balance) * toNum(amount)) / 100)
            : null;

    return (
        <>
            <Form.Group className="mb-2">
                <Form.Label>Напрямок</Form.Label>
                <div className="d-flex gap-2 mb-2">
                    <Button
                        size="sm"
                        variant={direction === "long" ? "success" : "outline-success"}
                        onClick={() => setDirection("long")}
                        className="flex-fill"
                    >
                        Long
                    </Button>
                    <Button
                        size="sm"
                        variant={direction === "short" ? "danger" : "outline-danger"}
                        onClick={() => setDirection("short")}
                        className="flex-fill"
                    >
                        Short
                    </Button>
                </div>
            </Form.Group>

            <Form.Group className="mb-2">
                <Form.Label>Розмір ставки</Form.Label>
                <div className="d-flex gap-2 mb-2">
                    <Button
                        size="sm"
                        variant={amountMode === "percent" ? "primary" : "outline-primary"}
                        onClick={() => setAmountMode("percent")}
                    >
                        % депозиту
                    </Button>
                    <Button
                        size="sm"
                        variant={amountMode === "usdt" ? "primary" : "outline-primary"}
                        onClick={() => setAmountMode("usdt")}
                    >
                        USDT
                    </Button>
                </div>
                <div className="d-flex gap-2">
                    <Form.Control
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        autoFocus
                    />
                    <OverlayTrigger
                        overlay={
                            <Tooltip>
                                {previousBet
                                    ? `×2 від попередньої ставки (${formatAmount(previousBet)})`
                                    : "Немає попередньої ставки на цю пару"}
                            </Tooltip>
                        }
                    >
                        <span>
                            <Button
                                variant="outline-secondary"
                                onClick={handleDouble}
                                disabled={!previousBet}
                                style={{ whiteSpace: "nowrap" }}
                            >
                                ×2
                            </Button>
                        </span>
                    </OverlayTrigger>
                </div>
                {amountUsdPreview != null && (
                    <div className="text-muted small mt-1">
                        ≈ {amountUsdPreview} USDT (від балансу {round2(toNum(balance))})
                    </div>
                )}
            </Form.Group>

            <div className="row g-2 mb-2">
                <div className="col-4">
                    <Form.Label>Тейк, %</Form.Label>
                    <Form.Control
                        type="number"
                        step="0.01"
                        value={take}
                        onChange={(e) => applyChange("take", e.target.value)}
                    />
                </div>
                <div className="col-4">
                    <Form.Label>Стоп, %</Form.Label>
                    <Form.Control
                        type="number"
                        step="0.01"
                        value={stop}
                        onChange={(e) => applyChange("stop", e.target.value)}
                    />
                </div>
                <div className="col-4">
                    <Form.Label>RR</Form.Label>
                    <Form.Control
                        type="number"
                        step="0.01"
                        value={rr}
                        onChange={(e) => applyChange("rr", e.target.value)}
                    />
                </div>
            </div>

            <Form.Group className="mb-1">
                <Form.Label>Плече</Form.Label>
                <Form.Control type="number" value={leverage} onChange={(e) => setLeverage(e.target.value)} />
            </Form.Group>
            {roiTarget != null && <div className="text-muted small mb-3">Ціль ROI: {roiTarget}%</div>}
        </>
    );
});

function AddBetModal({ show, onHide, onSubmit, previousBet, balance, defaultAmount }) {
    const [placedAt, setPlacedAt] = useState(nowForInput());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [resetKey, setResetKey] = useState(0);
    const economicsRef = useRef(null);

    useEffect(() => {
        if (show) {
            setPlacedAt(nowForInput());
            setError("");
            setResetKey((k) => k + 1);
        }
    }, [show]);

    const handleSubmit = async () => {
        const values = economicsRef.current.getValues();
        const amountValue = Number(values.amount);
        if (!Number.isFinite(amountValue) || amountValue <= 0) {
            setError("Вкажіть коректний розмір ставки");
            return;
        }
        setSaving(true);
        try {
            await onSubmit({ ...values, amount: amountValue, placedAt });
            onHide();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Нова ставка</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <div className="text-danger small mb-2">{error}</div>}
                <BetEconomicsFields
                    key={resetKey}
                    ref={economicsRef}
                    initial={{ ...DEFAULT_ECONOMICS, amount: String(defaultAmount) }}
                    previousBet={previousBet}
                    balance={balance}
                />
                <Form.Group>
                    <Form.Label>Дата й час ставки</Form.Label>
                    <Form.Control
                        type="datetime-local"
                        value={placedAt}
                        onChange={(e) => setPlacedAt(e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Скасувати
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={saving}>
                    Додати
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function EditBetModal({ bet, onHide, onSubmit, onDelete, balance }) {
    const [placedAt, setPlacedAt] = useState(nowForInput());
    const [status, setStatus] = useState("pending");
    const [resolvedAt, setResolvedAt] = useState(nowForInput());
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const economicsRef = useRef(null);

    useEffect(() => {
        if (bet) {
            setPlacedAt(toInputValue(bet.placedAt));
            setStatus(bet.status);
            setResolvedAt(toInputValue(bet.resolvedAt));
            setError("");
        }
    }, [bet]);

    const handleSubmit = async () => {
        const values = economicsRef.current.getValues();
        const amountValue = Number(values.amount);
        if (!Number.isFinite(amountValue) || amountValue <= 0) {
            setError("Вкажіть коректний розмір ставки");
            return;
        }
        // Тейк закривається за цільовим тейк-відсотком, стоп — за цільовим стоп-відсотком:
        // окремо "фактичний" відсоток руху ціни більше не запитуємо.
        const percentValue = status === "take" ? Number(values.takePercent) : undefined;
        if (status === "take" && !Number.isFinite(percentValue)) {
            setError("Вкажіть відсоток тейку в калькуляторі вище");
            return;
        }
        setSaving(true);
        try {
            await onSubmit(bet._id, {
                ...values,
                amount: amountValue,
                placedAt,
                status,
                percent: percentValue,
                resolvedAt: status === "pending" ? undefined : resolvedAt,
            });
            onHide();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await onDelete(bet._id);
            onHide();
        } catch (e) {
            setError(e.message);
            setDeleting(false);
        }
    };

    return (
        <Modal show={!!bet} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Редагувати ставку</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <div className="text-danger small mb-2">{error}</div>}
                {bet && (
                    <BetEconomicsFields
                        key={bet._id}
                        ref={economicsRef}
                        initial={{
                            amountMode: bet.amountMode || "percent",
                            direction: bet.direction || "long",
                            amount: String(bet.amount),
                            leverage: bet.leverage != null ? String(bet.leverage) : "50",
                            takePercent: bet.takePercent != null ? String(bet.takePercent) : "2",
                            stopPercent: bet.stopPercent != null ? String(bet.stopPercent) : "2",
                            rr: bet.rr != null ? String(bet.rr) : "1",
                        }}
                        balance={balance}
                    />
                )}
                <Form.Group className="mb-3">
                    <Form.Label>Дата й час ставки</Form.Label>
                    <Form.Control
                        type="datetime-local"
                        value={placedAt}
                        onChange={(e) => setPlacedAt(e.target.value)}
                    />
                </Form.Group>

                <Form.Label>Статус</Form.Label>
                <div className="d-flex gap-2 mb-3">
                    <Button
                        variant={status === "pending" ? "primary" : "outline-primary"}
                        onClick={() => setStatus("pending")}
                        className="flex-fill"
                    >
                        Очікує
                    </Button>
                    <Button
                        variant={status === "take" ? "success" : "outline-success"}
                        onClick={() => setStatus("take")}
                        className="flex-fill"
                    >
                        Тейк
                    </Button>
                    <Button
                        variant={status === "stop" ? "danger" : "outline-danger"}
                        onClick={() => setStatus("stop")}
                        className="flex-fill"
                    >
                        Стоп
                    </Button>
                    <Button
                        variant={status === "cancelled" ? "dark" : "outline-dark"}
                        onClick={() => setStatus("cancelled")}
                        className="flex-fill"
                    >
                        Скасовано
                    </Button>
                </div>
                {status !== "pending" && (
                    <Form.Group>
                        <Form.Label>Дата й час тейку/стопу</Form.Label>
                        <Form.Control
                            type="datetime-local"
                            value={resolvedAt}
                            onChange={(e) => setResolvedAt(e.target.value)}
                        />
                    </Form.Group>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-danger"
                    onClick={handleDelete}
                    disabled={saving || deleting}
                    className="me-auto"
                >
                    <Trash2 size={14} className="me-1" />
                    Видалити ставку
                </Button>
                <Button variant="secondary" onClick={onHide} disabled={saving || deleting}>
                    Скасувати
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={saving || deleting}>
                    Зберегти
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function DeletePairModal({ pair, onHide, onConfirm }) {
    const [saving, setSaving] = useState(false);

    const handleConfirm = async () => {
        setSaving(true);
        try {
            await onConfirm(pair._id);
            onHide();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={!!pair} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Видалити пару</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                Видалити пару <strong>{pair?.symbol}</strong> та всі її ставки? Дію не можна скасувати.
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Скасувати
                </Button>
                <Button variant="danger" onClick={handleConfirm} disabled={saving}>
                    Видалити
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function SettingsModal({
    show,
    onHide,
    depositInput,
    setDepositInput,
    savingDeposit,
    onSaveDeposit,
    depositError,
    baseAmountInput,
    setBaseAmountInput,
    savingBaseAmount,
    onSaveBaseAmount,
    baseAmountError,
    baseAmount,
    sizingMode,
    setSizingMode,
    locked,
    setLocked,
    deleteLocked,
    setDeleteLocked,
    newSymbol,
    setNewSymbol,
    addingPair,
    pairError,
    onAddPair,
}) {
    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Налаштування</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={onSaveDeposit} className="d-flex gap-2 align-items-end mb-2">
                    <Form.Group className="flex-fill">
                        <Form.Label>Стартовий депозит, USDT</Form.Label>
                        <Form.Control
                            type="number"
                            value={depositInput}
                            onChange={(e) => setDepositInput(e.target.value)}
                        />
                    </Form.Group>
                    <Button type="submit" variant="outline-primary" disabled={savingDeposit}>
                        Зберегти
                    </Button>
                </Form>
                {depositError && <div className="text-danger small mb-3">{depositError}</div>}

                <Form onSubmit={onSaveBaseAmount} className="d-flex gap-2 align-items-end mb-2">
                    <Form.Group className="flex-fill">
                        <Form.Label>Базова ставка, USDT</Form.Label>
                        <Form.Control
                            type="number"
                            value={baseAmountInput}
                            onChange={(e) => setBaseAmountInput(e.target.value)}
                        />
                    </Form.Group>
                    <Button type="submit" variant="outline-primary" disabled={savingBaseAmount}>
                        Зберегти
                    </Button>
                </Form>
                {baseAmountError && <div className="text-danger small mb-3">{baseAmountError}</div>}

                <Form onSubmit={onAddPair} className="d-flex gap-2 align-items-start mb-3">
                    <div className="flex-fill">
                        <Form.Label>Додати пару</Form.Label>
                        <Form.Control
                            placeholder="Назва пари (напр. BTCUSDT або LDOUSDT.P)"
                            value={newSymbol}
                            onChange={(e) => setNewSymbol(e.target.value.replace(/[^a-zA-Z0-9.]/g, ""))}
                        />
                        {pairError && <div className="text-danger small mt-1">{pairError}</div>}
                    </div>
                    <Button type="submit" disabled={addingPair || !newSymbol.trim()} style={{ marginTop: 32 }}>
                        Додати
                    </Button>
                </Form>

                <Form.Group className="mb-3">
                    <Form.Label>Режим перегляду статистики</Form.Label>
                    <div className="d-flex gap-2">
                        <Button
                            size="sm"
                            variant={sizingMode === "fixed" ? "primary" : "outline-secondary"}
                            onClick={() => setSizingMode("fixed")}
                            className="flex-fill"
                        >
                            Фікс (${baseAmount})
                        </Button>
                        <Button
                            size="sm"
                            variant={sizingMode === "percent" ? "primary" : "outline-secondary"}
                            onClick={() => setSizingMode("percent")}
                            className="flex-fill"
                        >
                            Відсоток ({WHATIF_BASE_PERCENT}%)
                        </Button>
                    </div>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Кнопки</Form.Label>
                    <div className="d-flex gap-2">
                        <OverlayTrigger
                            overlay={
                                <Tooltip>
                                    {locked
                                        ? "Ставки заблоковано від випадкових кліків"
                                        : "Заблокувати клік по ставках, щоб не змінити статус випадково"}
                                </Tooltip>
                            }
                        >
                            <Button
                                size="sm"
                                variant={locked ? "dark" : "outline-secondary"}
                                onClick={() => setLocked((v) => !v)}
                                className="d-flex align-items-center justify-content-center gap-2 flex-fill"
                            >
                                {locked ? <Lock size={14} /> : <Unlock size={14} />}
                                {locked ? "Заблоковано" : "Розблоковано"}
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                            overlay={
                                <Tooltip>
                                    {deleteLocked
                                        ? "Кнопки видалення сховано"
                                        : "Сховати кнопки видалення пар і ставок"}
                                </Tooltip>
                            }
                        >
                            <Button
                                size="sm"
                                variant={deleteLocked ? "dark" : "outline-secondary"}
                                onClick={() => setDeleteLocked((v) => !v)}
                                className="d-flex align-items-center justify-content-center gap-2 flex-fill"
                            >
                                {deleteLocked ? <Lock size={14} /> : <Trash2 size={14} />}
                                {deleteLocked ? "Видалення сховано" : "Видалення видно"}
                            </Button>
                        </OverlayTrigger>
                    </div>
                </Form.Group>
            </Modal.Body>
        </Modal>
    );
}

const CHIP_DIAMETER = 30; // попередні 20px + 5px до радіуса з обох боків
const CHIP_BOX = CHIP_DIAMETER + 4; // трохи більший hover-хітбокс, щоб хрестик не "блимав"

const CHIP_CORNER_BTN = {
    position: "absolute",
    width: 16,
    height: 16,
    borderRadius: "50%",
    border: "none",
    background: "#495057",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
};

const STATUS_LABEL = { pending: "Ще не спрацювала", take: "Тейк", stop: "Стоп", cancelled: "Скасована" };

function BetChip({ bet, onCycle, onEdit, onDelete, onScreenshots, locked, deleteLocked }) {
    const [hovered, setHovered] = useState(false);
    const isCancelled = bet.status === "cancelled";
    const tooltipLines = [
        `${bet.direction === "short" ? "Short" : "Long"} · ${formatAmount(bet)} (плече x${bet.leverage})`,
        `Тейк/Стоп ціль: ${bet.takePercent}% / ${bet.stopPercent}% (RR ${bet.rr})`,
        `Розміщена: ${formatDateTime(bet.placedAt)}`,
    ];
    if (bet.status === "pending") {
        tooltipLines.push(STATUS_LABEL.pending);
    } else {
        tooltipLines.push(bet.status === "take" ? `Тейк: +${bet.percent}%` : STATUS_LABEL[bet.status]);
        tooltipLines.push(`Закрита: ${formatDateTime(bet.resolvedAt)}`);
    }
    tooltipLines.push(locked ? "Ставки заблоковано" : "Клік — змінити статус");

    return (
        <div
            style={{ position: "relative", width: CHIP_BOX, height: CHIP_BOX, flexShrink: 0 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip>
                        {tooltipLines.map((line) => (
                            <div key={line}>{line}</div>
                        ))}
                    </Tooltip>
                }
            >
                <div
                    onClick={locked ? undefined : onCycle}
                    role="button"
                    style={{
                        position: "absolute",
                        top: (CHIP_BOX - CHIP_DIAMETER) / 2,
                        left: (CHIP_BOX - CHIP_DIAMETER) / 2,
                        width: CHIP_DIAMETER,
                        height: CHIP_DIAMETER,
                        borderRadius: "50%",
                        backgroundColor: STATUS_COLOR[bet.status],
                        border: isCancelled ? "2px solid #000" : "none",
                        boxSizing: "border-box",
                        cursor: locked ? "not-allowed" : "pointer",
                        opacity: locked ? 0.85 : 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: isCancelled ? "#000" : "#fff",
                        fontSize: 13,
                        fontWeight: 700,
                        lineHeight: 1,
                        userSelect: "none",
                    }}
                >
                    {DIRECTION_LETTER[bet.direction] || "L"}
                </div>
            </OverlayTrigger>
            {hovered && !locked && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                    title="Редагувати ставку"
                    style={{ ...CHIP_CORNER_BTN, top: 0, left: 0 }}
                >
                    <Pencil size={9} />
                </button>
            )}
            {hovered && !locked && !deleteLocked && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    title="Видалити ставку"
                    style={{ ...CHIP_CORNER_BTN, top: 0, right: 0 }}
                >
                    <X size={10} />
                </button>
            )}
            {hovered && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onScreenshots();
                    }}
                    title="Скріншоти"
                    style={{ ...CHIP_CORNER_BTN, bottom: 0, left: 0 }}
                >
                    <Plus size={10} />
                </button>
            )}
        </div>
    );
}

const SCREENSHOT_MAX_DIMENSION = 1800;

function readClipboardImageFile(e) {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
        if (item.type && item.type.startsWith("image/")) {
            return item.getAsFile();
        }
    }
    return null;
}

function downscaleImageToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
            const scale = Math.min(1, SCREENSHOT_MAX_DIMENSION / Math.max(img.width, img.height));
            const width = Math.round(img.width * scale);
            const height = Math.round(img.height * scale);
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            canvas.getContext("2d").drawImage(img, 0, 0, width, height);
            URL.revokeObjectURL(objectUrl);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = (err) => {
            URL.revokeObjectURL(objectUrl);
            reject(err);
        };
        img.src = objectUrl;
    });
}


// Одразу після завантаження прев'ю на Google CDN ще може бути не готове (генерується асинхронно) —
// браузер сам повторний запит не робить, тому ретраїмо `<img>` кілька разів з паузою.
function RetryImage({ src, alt, onClick, style }) {
    const MAX_RETRIES = 8;
    const RETRY_DELAY_MS = 1500;
    const [attempt, setAttempt] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setAttempt(0);
        setLoaded(false);
        setFailed(false);
    }, [src]);

    const handleError = () => {
        if (attempt >= MAX_RETRIES) {
            setFailed(true);
            return;
        }
        setTimeout(() => setAttempt((a) => a + 1), RETRY_DELAY_MS);
    };

    if (failed) {
        return (
            <div
                style={{
                    ...style,
                    minHeight: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#222",
                    color: "#aaa",
                }}
            >
                Не вдалося завантажити зображення
            </div>
        );
    }

    return (
        <>
            {!loaded && (
                <div
                    style={{
                        minHeight: 300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#999",
                    }}
                >
                    Завантаження…
                </div>
            )}
            <img
                key={attempt}
                src={src}
                alt={alt}
                onClick={onClick}
                onLoad={() => setLoaded(true)}
                onError={handleError}
                style={{ ...style, display: loaded ? style?.display || "block" : "none" }}
            />
        </>
    );
}

// react-slick клонує prevArrow/nextArrow, підставляючи currentSlide/slideCount/onClick —
// рендеримо null на межі, щоб стрілка в бік, де більше немає слайдів, взагалі не показувалась.
function ScreenshotArrow({ direction, currentSlide, slideCount, onClick }) {
    const isPrev = direction === "prev";
    const atEdge = isPrev ? currentSlide === 0 : currentSlide >= slideCount - 1;
    if (atEdge) return null;
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={isPrev ? "Попередній скріншот" : "Наступний скріншот"}
            style={{
                position: "absolute",
                top: "50%",
                [isPrev ? "left" : "right"]: 8,
                transform: "translateY(-50%)",
                zIndex: 5,
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "none",
                background: "rgba(0,0,0,0.55)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            }}
        >
            {isPrev ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
        </button>
    );
}

function ScreenshotsModal({ bet, onHide, onUpload, onDelete }) {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const sliderRef = useRef(null);
    const sliderBoxRef = useRef(null);
    const wheelCooldownRef = useRef(false);
    const betId = bet?._id;
    const screenshots = bet?.screenshots || [];

    useEffect(() => {
        if (!betId) return undefined;

        const handlePaste = async (e) => {
            const file = readClipboardImageFile(e);
            if (!file) return;
            e.preventDefault();
            setUploading(true);
            setError("");
            try {
                const dataUrl = await downscaleImageToDataUrl(file);
                await onUpload(betId, dataUrl);
            } catch (err) {
                setError(err.message);
            } finally {
                setUploading(false);
            }
        };

        window.addEventListener("paste", handlePaste);
        return () => window.removeEventListener("paste", handlePaste);
    }, [betId, onUpload]);

    // Стрілки клавіатури — коли відкритий лайтбокс, гортають повнорозмірний перегляд;
    // інакше перемикають слайд у модалці.
    useEffect(() => {
        if (!betId) return undefined;

        const handleKeyDown = (e) => {
            if (lightboxIndex !== null) {
                if (e.key === "ArrowLeft") setLightboxIndex((i) => Math.max(0, i - 1));
                else if (e.key === "ArrowRight") setLightboxIndex((i) => Math.min(screenshots.length - 1, i + 1));
                else if (e.key === "Escape") setLightboxIndex(null);
                return;
            }
            if (e.key === "ArrowLeft") sliderRef.current?.slickPrev();
            else if (e.key === "ArrowRight") sliderRef.current?.slickNext();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [betId, lightboxIndex, screenshots.length]);

    // Свайп трекпадом / горизонтальна прокрутка Magic Mouse приходять як wheel-подія з deltaX.
    // Кулдаун потрібен, бо один фізичний свайп генерує десятки wheel-подій підряд.
    useEffect(() => {
        const el = sliderBoxRef.current;
        if (!betId || !el) return undefined;

        const handleWheel = (e) => {
            if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || Math.abs(e.deltaX) < 10) return;
            e.preventDefault();
            if (wheelCooldownRef.current) return;
            wheelCooldownRef.current = true;
            if (e.deltaX > 0) sliderRef.current?.slickNext();
            else sliderRef.current?.slickPrev();
            setTimeout(() => {
                wheelCooldownRef.current = false;
            }, 400);
        };

        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, [betId, screenshots.length]);

    useEffect(() => {
        if (sliderRef.current && screenshots.length) {
            sliderRef.current.slickGoTo(screenshots.length - 1);
        }
    }, [screenshots.length]);

    const handleDelete = async (screenshotId) => {
        setError("");
        try {
            await onDelete(betId, screenshotId);
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <>
        <Modal show={!!bet} onHide={onHide} centered size="lg" keyboard={lightboxIndex === null}>
            <Modal.Body className="p-0 position-relative">
                <style>{`
                    .trading-screenshot-dots { pointer-events: none; }
                    .trading-screenshot-dots li { pointer-events: auto; width: auto; height: auto; margin: 0; }
                    .trading-screenshot-dots li div { transition: transform 0.15s ease, background-color 0.15s ease; }
                    .trading-screenshot-dots li.slick-active div { background: #0d6efd; box-shadow: 0 0 0 2px rgba(255,255,255,0.9); transform: scale(1.3); }
                `}</style>
                <button
                    type="button"
                    onClick={onHide}
                    title="Закрити"
                    style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 10,
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        border: "none",
                        background: "rgba(0,0,0,0.55)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                    }}
                >
                    <X size={16} />
                </button>
                {(error || uploading) && (
                    <div
                        style={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            right: 48,
                            zIndex: 9,
                            background: "rgba(0,0,0,0.6)",
                            color: error ? "#ff8a8a" : "#fff",
                            fontSize: 13,
                            padding: "6px 10px",
                            borderRadius: 6,
                        }}
                    >
                        {error || "Завантаження…"}
                    </div>
                )}
                {screenshots.length === 0 ? (
                    <div className="text-muted text-center py-5 px-3">
                        Скопіюйте зображення і натисніть Cmd+V (Ctrl+V), щоб зберегти скріншот
                    </div>
                ) : (
                    <div ref={sliderBoxRef} className="trading-screenshot-modal">
                        <Slider
                            ref={sliderRef}
                            dots
                            arrows
                            dotsClass="trading-screenshot-dots"
                            prevArrow={<ScreenshotArrow direction="prev" />}
                            nextArrow={<ScreenshotArrow direction="next" />}
                            infinite={false}
                            speed={200}
                            slidesToShow={1}
                            slidesToScroll={1}
                            adaptiveHeight
                            appendDots={(dots) => (
                                <ul
                                    className="trading-screenshot-dots"
                                    style={{
                                        position: "absolute",
                                        bottom: 10,
                                        left: 0,
                                        right: 0,
                                        margin: 0,
                                        padding: 0,
                                        display: "flex",
                                        justifyContent: "center",
                                        gap: 6,
                                        listStyle: "none",
                                        zIndex: 5,
                                    }}
                                >
                                    {dots}
                                </ul>
                            )}
                            customPaging={() => (
                                <div
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: "rgba(255,255,255,0.6)",
                                        boxShadow: "0 0 2px rgba(0,0,0,0.6)",
                                        cursor: "pointer",
                                    }}
                                />
                            )}
                        >
                            {screenshots.map((s, index) => (
                                <div key={s._id}>
                                    <div style={{ position: "relative" }}>
                                        <RetryImage
                                            src={s.url}
                                            alt={s.filename}
                                            onClick={() => setLightboxIndex(index)}
                                            style={{ width: "100%", display: "block", cursor: "zoom-in" }}
                                        />
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleDelete(s._id)}
                                            title="Видалити скріншот"
                                            style={{ position: "absolute", bottom: 8, right: 8, zIndex: 5 }}
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>
                )}
            </Modal.Body>
        </Modal>
        {lightboxIndex !== null && screenshots[lightboxIndex] && (
            <div
                onClick={() => setLightboxIndex(null)}
                style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0,0,0,0.9)",
                    zIndex: 2000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "zoom-out",
                }}
            >
                <RetryImage
                    src={screenshots[lightboxIndex].url}
                    alt={screenshots[lightboxIndex].filename}
                    onClick={() => setLightboxIndex(null)}
                    style={{ maxWidth: "95vw", maxHeight: "95vh", objectFit: "contain", cursor: "zoom-out" }}
                />
            </div>
        )}
        </>
    );
}

const TradingPage = () => {
    const [pairs, setPairs] = useState([]);
    const [deposit, setDeposit] = useState(0);
    const [depositInput, setDepositInput] = useState("0");
    const [savingDeposit, setSavingDeposit] = useState(false);
    const [depositError, setDepositError] = useState("");
    const [baseAmount, setBaseAmount] = useState(5);
    const [baseAmountInput, setBaseAmountInput] = useState("5");
    const [savingBaseAmount, setSavingBaseAmount] = useState(false);
    const [baseAmountError, setBaseAmountError] = useState("");
    const [loading, setLoading] = useState(true);
    const [newSymbol, setNewSymbol] = useState("");
    const [addingPair, setAddingPair] = useState(false);
    const [pairError, setPairError] = useState("");
    const [addBetPairId, setAddBetPairId] = useState(null);
    const [editBet, setEditBet] = useState(null);
    const [deletePair, setDeletePair] = useState(null);
    const [locked, setLocked] = useState(true);
    const [deleteLocked, setDeleteLocked] = useState(true);
    const [screenshotsBetId, setScreenshotsBetId] = useState(null);
    const [showSettings, setShowSettings] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getTradingData();
            setDeposit(data.deposit);
            setDepositInput(String(data.deposit));
            setBaseAmount(data.baseAmount);
            setBaseAmountInput(String(data.baseAmount));
            setPairs(data.pairs);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // "Фікс" — реальний записаний журнал (той, що й раніше). "Відсоток" — той самий реплей,
    // але "а що якби" я завжди ставив WHATIF_BASE_PERCENT% від балансу замість фіксованих $5.
    // Реальний баланс (fixedHistory) завжди рахується окремо — саме він, а не активний режим
    // перегляду, використовується для реального розміру нових ставок (модалки нижче).
    const [sizingMode, setSizingMode] = useState("fixed");
    const fixedHistory = useMemo(() => computeBalanceHistory(deposit, pairs), [deposit, pairs]);
    const percentHistory = useMemo(() => computeBalanceHistoryPercent(deposit, pairs), [deposit, pairs]);
    const weeklyActivity = useMemo(() => computeWeeklyActivity(pairs), [pairs]);
    const lastUpdated = useMemo(() => {
        const timestamps = pairs.flatMap((p) => p.bets).map((b) => new Date(b.updatedAt).getTime()).filter((t) => !Number.isNaN(t));
        return timestamps.length ? new Date(Math.max(...timestamps)) : null;
    }, [pairs]);
    const activeHistory = sizingMode === "fixed" ? fixedHistory : percentHistory;
    const currentBalance = activeHistory.balance;
    const realBalance = fixedHistory.balance;

    // Перегляд журналу за період: "Весь час" (з дати заведення журналу), 7д, 10д,
    // цей тиждень, минулий тиждень. Впливає на ROI, зміну балансу, тейк/стоп, winrate
    // та серії нижче — усе рахується заново від закритих ставок усередині періоду.
    const [period, setPeriod] = useState("all");
    const [customRange, setCustomRange] = useState({ from: "", to: "" });
    const periodRangeValue = useMemo(() => periodRange(period, customRange), [period, customRange]);

    const allResolvedBets = useMemo(() => resolvedBetsChronological(pairs), [pairs]);
    const periodBets = useMemo(() => {
        const { start, end } = periodRangeValue;
        return allResolvedBets.filter((bet) => {
            const t = dayjs(bet.resolvedAt);
            if (start && t.isBefore(start)) return false;
            if (end && t.isAfter(end)) return false;
            return true;
        });
    }, [allResolvedBets, periodRangeValue]);

    const tradeStats = useMemo(() => {
        let wins = 0;
        let losses = 0;
        for (const bet of periodBets) {
            if (bet.status === "take") wins++;
            else if (bet.status === "stop") losses++;
        }
        const total = wins + losses;
        return { wins, losses, winRate: total > 0 ? round2((wins / total) * 100) : null };
    }, [periodBets]);

    const streakStats = useMemo(() => computeStreaks(periodBets), [periodBets]);

    // Баланс на початок і на кінець періоду (за реальним "фікс"-реплеєм, незалежно від
    // активного режиму перегляду percent/fixed нижче) — звідси і ROI, і зміна в $ за період.
    const periodBalance = useMemo(() => {
        const { start, end } = periodRangeValue;
        const startBalance = start
            ? balanceAsOf(fixedHistory.points, deposit, start.subtract(1, "millisecond"))
            : round2(Number(deposit) || 0);
        const endBalance = balanceAsOf(fixedHistory.points, deposit, end);
        const pnl = round2(endBalance - startBalance);
        const periodRoi = startBalance > 0 ? round2((pnl / startBalance) * 100) : null;
        return { startBalance, endBalance, pnl, roi: periodRoi };
    }, [fixedHistory.points, deposit, periodRangeValue]);

    const roi = periodBalance.roi;

    // Точки для графіка балансу, обрізані під обраний період — інакше плитки статистики
    // (зміна за період, ROI тощо) перераховуються, а графік під ними мовчки далі малює
    // весь час з дати заведення журналу, що вводить в оману.
    const periodBalancePoints = useMemo(() => {
        const { start, end } = periodRangeValue;
        const inRange = activeHistory.points.filter((p) => {
            if (!p.time) return false;
            const t = dayjs(p.time);
            if (start && t.isBefore(start)) return false;
            if (end && t.isAfter(end)) return false;
            return true;
        });
        const startBalance = start
            ? balanceAsOf(activeHistory.points, deposit, start.subtract(1, "millisecond"))
            : round2(Number(activeHistory.points[0]?.balance ?? deposit) || 0);
        const startPoint = { time: start ? start.toISOString() : null, balance: startBalance };
        return [startPoint, ...inRange];
    }, [activeHistory.points, deposit, periodRangeValue]);

    // Скільки реально зароблено (чистий pnl мінус комісії) на кожній парі за весь час —
    // за "фікс"-реплеєм незалежно від активного режиму перегляду percent/fixed, бо це має
    // відображати реальні гроші, а не симуляцію "а що якби".
    const pairEarnedMap = useMemo(() => {
        const map = new Map();
        for (const p of fixedHistory.points) {
            if (!p.time || !p.pairSymbol) continue;
            map.set(p.pairSymbol, (map.get(p.pairSymbol) || 0) + (p.pnl || 0) - (p.fee || 0));
        }
        return map;
    }, [fixedHistory.points]);

    // Сума сплачених комісій (Binance Futures maker+taker з номіналу margin×leverage,
    // уже врахована у periodBalance.pnl вище) — окремо, лише для наочності.
    const periodFees = useMemo(() => {
        const { start, end } = periodRangeValue;
        let total = 0;
        for (const p of fixedHistory.points) {
            if (!p.time) continue;
            const t = dayjs(p.time);
            if (start && t.isBefore(start)) continue;
            if (end && t.isAfter(end)) continue;
            total += p.fee || 0;
        }
        return round2(total);
    }, [fixedHistory.points, periodRangeValue]);

    // Середній заробіток на день за період: pnl періоду поділений на кількість днів у ньому.
    // Для "all" (start === null) днем відліку береться resolvedAt найпершої закритої ставки —
    // до заведення журналу ставок все одно немає. Кількість днів завжди округлюється вгору і
    // не менше 1 — інакше "Сьогодні"/"Вчора" (менше доби) ділили б pnl на дробове число,
    // штучно завищуючи середнє.
    const avgPnlPerDay = useMemo(() => {
        const { start, end } = periodRangeValue;
        const rangeStart = start || (allResolvedBets[0] ? dayjs(allResolvedBets[0].resolvedAt) : end);
        const days = Math.max(1, Math.ceil(end.diff(rangeStart, "day", true)));
        return round2(periodBalance.pnl / days);
    }, [periodBalance.pnl, periodRangeValue, allResolvedBets]);

    // Ставки в таблиці нижче показуємо лише ті, що потрапляють в обраний період (за
    // placedAt — датою відкриття, а не resolvedAt, інакше pending-ставки без resolvedAt
    // випадали б з таблиці). При "Весь час" (start і end обидва null) — не фільтруємо
    // взагалі, щоб не ховати пари без жодної ставки. В інших періодах пари, у яких після
    // фільтрації не лишилось жодної ставки, прибираємо з таблиці як шум.
    const periodFilteredPairs = useMemo(() => {
        const { start, end } = periodRangeValue;
        if (!start && !end) return pairs;
        return pairs
            .map((pair) => ({
                ...pair,
                bets: pair.bets.filter((bet) => {
                    const t = dayjs(bet.placedAt);
                    if (start && t.isBefore(start)) return false;
                    if (end && t.isAfter(end)) return false;
                    return true;
                }),
            }))
            .filter((pair) => pair.bets.length > 0);
    }, [pairs, periodRangeValue]);

    const [pairSort, setPairSort] = useState({ key: "open", dir: "desc" });
    const [groupByDay, setGroupByDay] = useState(false);
    // Колонки-дні для режиму "Групувати по днях" — спільні для всіх пар, інакше колонки
    // в різних рядках таблиці не збігалися б. Найновіший день — лівою колонкою (так само,
    // як і порядок ставок всередині кожного дня, і як флет-режим без групування).
    const dayColumns = useMemo(() => {
        if (!groupByDay) return [];
        const days = new Set();
        for (const pair of periodFilteredPairs) {
            for (const bet of pair.bets) {
                days.add(dayjs(bet.placedAt).format("YYYY-MM-DD"));
            }
        }
        return Array.from(days).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
    }, [periodFilteredPairs, groupByDay]);

    // Ширина кожної day-колонки визначається найбільшою кількістю ставок, яку в цей день
    // має якась одна пара (ставки всередині дня йдуть у рядок, а не одна під одною) —
    // інакше пари з кількома угодами за день не влізали б у вузьку колонку.
    const dayColumnMaxCount = useMemo(() => {
        if (!groupByDay) return {};
        const counts = {};
        for (const pair of periodFilteredPairs) {
            const perDay = {};
            for (const bet of pair.bets) {
                const day = dayjs(bet.placedAt).format("YYYY-MM-DD");
                perDay[day] = (perDay[day] || 0) + 1;
            }
            for (const [day, count] of Object.entries(perDay)) {
                counts[day] = Math.max(counts[day] || 0, count);
            }
        }
        return counts;
    }, [periodFilteredPairs, groupByDay]);

    const dayColumnWidth = (day) => {
        const count = Math.max(1, dayColumnMaxCount[day] || 1);
        return count * CHIP_BOX + (count - 1) * 8 + 8;
    };

    const togglePairSort = (key) => {
        setPairSort((prev) => {
            if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
            // За замовчуванням: A-Я від початку алфавіту, winrate/кількість — від найбільшого.
            return { key, dir: key === "symbol" ? "asc" : "desc" };
        });
    };

    // Ліміт pending-ставок на пару — це жорстке бізнес-правило (сервер його теж перевіряє),
    // тож рахуємо завжди по ПОВНОМУ набору ставок пари, а не по period-відфільтрованому —
    // інакше кнопка "додати ставку" могла б розблокуватись просто через перегляд вузького
    // періоду, у якому частина pending-ставок пари не показана.
    const pendingCountByPairId = useMemo(() => {
        const map = new Map();
        for (const pair of pairs) {
            map.set(pair._id, pair.bets.filter((b) => b.status === "pending").length);
        }
        return map;
    }, [pairs]);

    const sortedPairs = useMemo(() => {
        if (!pairSort.key) return periodFilteredPairs;
        const dirMul = pairSort.dir === "asc" ? 1 : -1;
        const withRate = periodFilteredPairs.map((pair) => ({ pair, rate: pairWinRate(pair) }));
        withRate.sort((a, b) => {
            if (pairSort.key === "symbol") {
                return a.pair.symbol.localeCompare(b.pair.symbol) * dirMul;
            }
            if (pairSort.key === "count") {
                return (a.pair.bets.length - b.pair.bets.length) * dirMul;
            }
            if (pairSort.key === "open") {
                const aPending = a.pair.bets.some((bet) => bet.status === "pending");
                const bPending = b.pair.bets.some((bet) => bet.status === "pending");
                if (aPending !== bPending) return aPending ? -1 : 1;
                return pairCurrentLossStreak(b.pair) - pairCurrentLossStreak(a.pair);
            }
            if (pairSort.key === "earned") {
                const aEarned = pairEarnedMap.get(a.pair.symbol) || 0;
                const bEarned = pairEarnedMap.get(b.pair.symbol) || 0;
                return (aEarned - bEarned) * dirMul;
            }
            // winrate: пари без жодної закритої ставки — завжди в кінці, незалежно від напрямку.
            if (a.rate == null && b.rate == null) return 0;
            if (a.rate == null) return 1;
            if (b.rate == null) return -1;
            return (a.rate - b.rate) * dirMul;
        });
        return withRate.map((x) => x.pair);
    }, [periodFilteredPairs, pairSort, pairEarnedMap]);

    const handleSaveDeposit = async (e) => {
        e.preventDefault();
        const value = Number(depositInput);
        if (!Number.isFinite(value) || value < 0) {
            setDepositError("Вкажіть коректний баланс");
            return;
        }
        setSavingDeposit(true);
        setDepositError("");
        try {
            const res = await updateTradingDeposit(value);
            setDeposit(res.deposit);
        } catch (e) {
            setDepositError(e.message);
        } finally {
            setSavingDeposit(false);
        }
    };

    const handleSaveBaseAmount = async (e) => {
        e.preventDefault();
        const value = Number(baseAmountInput);
        if (!Number.isFinite(value) || value <= 0) {
            setBaseAmountError("Вкажіть коректну базову ставку");
            return;
        }
        setSavingBaseAmount(true);
        setBaseAmountError("");
        try {
            const res = await updateTradingBaseAmount(value);
            setBaseAmount(res.baseAmount);
        } catch (e) {
            setBaseAmountError(e.message);
        } finally {
            setSavingBaseAmount(false);
        }
    };

    const handleAddPair = async (e) => {
        e.preventDefault();
        const symbol = newSymbol.trim().toUpperCase();
        if (!symbol) return;
        setAddingPair(true);
        setPairError("");
        try {
            const pair = await createTradingPair(symbol);
            setPairs((prev) => [...prev, pair]);
            setNewSymbol("");
        } catch (e) {
            setPairError(e.message);
        } finally {
            setAddingPair(false);
        }
    };

    const sortByPlacedAt = (bets) =>
        [...bets].sort((a, b) => new Date(a.placedAt) - new Date(b.placedAt));

    const handleAddBet = async (payload) => {
        const bet = await createTradingBet(addBetPairId, payload);
        setPairs((prev) =>
            prev.map((p) =>
                p._id === addBetPairId ? { ...p, bets: sortByPlacedAt([...p.bets, bet]) } : p
            )
        );
    };

    const handleEditBet = async (betId, payload) => {
        const updated = await updateTradingBet(betId, payload);
        setPairs((prev) =>
            prev.map((p) => ({
                ...p,
                bets: sortByPlacedAt(p.bets.map((b) => (b._id === betId ? updated : b))),
            }))
        );
    };

    const handleCycleBetStatus = async (bet) => {
        const nextStatus = STATUS_CYCLE[(STATUS_CYCLE.indexOf(bet.status) + 1) % STATUS_CYCLE.length];
        const payload = { status: nextStatus };
        if (nextStatus === "take") {
            payload.percent = bet.takePercent;
            payload.resolvedAt = nowForInput();
        } else if (nextStatus === "stop" || nextStatus === "cancelled") {
            payload.resolvedAt = nowForInput();
        }
        try {
            await handleEditBet(bet._id, payload);
        } catch (e) {
            window.alert(e.message);
        }
    };

    const handleDeletePair = async (pairId) => {
        await deleteTradingPair(pairId);
        setPairs((prev) => prev.filter((p) => p._id !== pairId));
    };

    const handleDeleteBet = async (betId) => {
        await deleteTradingBet(betId);
        setPairs((prev) =>
            prev.map((p) => ({ ...p, bets: p.bets.filter((b) => b._id !== betId) }))
        );
    };

    const mergeUpdatedBet = (updated) => {
        setPairs((prev) =>
            prev.map((p) => ({
                ...p,
                bets: p.bets.map((b) => (b._id === updated._id ? updated : b)),
            }))
        );
    };

    const handleUploadScreenshot = async (betId, imageBase64) => {
        const updated = await uploadBetScreenshot(betId, imageBase64);
        mergeUpdatedBet(updated);
    };

    const handleDeleteScreenshot = async (betId, screenshotId) => {
        const updated = await deleteBetScreenshot(betId, screenshotId);
        mergeUpdatedBet(updated);
    };

    if (loading) return <Loader />;

    const addBetPair = pairs.find((p) => p._id === addBetPairId);
    const previousBetForAdd = addBetPair && addBetPair.bets.length ? addBetPair.bets[addBetPair.bets.length - 1] : null;
    const screenshotsBet = pairs.flatMap((p) => p.bets).find((b) => b._id === screenshotsBetId) || null;

    return (
        <div className="container-fluid py-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="mb-0">Журнал</h3>
                    {lastUpdated && (
                        <div className="text-muted small">Журнал оновлено: {formatDateTime(lastUpdated)}</div>
                    )}
                </div>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowSettings(true)}
                    title="Налаштування"
                    className="d-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34, padding: 0 }}
                >
                    <Settings size={16} />
                </Button>
            </div>

            {sizingMode === "percent" && percentHistory.ruinedAt && (
                <div className="text-danger small mb-2">
                    ⚠ У цьому режимі рахунок обнулився б {formatDateTime(percentHistory.ruinedAt)}
                </div>
            )}

            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                {PERIODS.map(({ key, label }) => (
                    <Button
                        key={key}
                        size="sm"
                        variant={period === key ? "primary" : "outline-secondary"}
                        onClick={() => setPeriod(key)}
                    >
                        {label}
                    </Button>
                ))}
                {period === "custom" && (
                    <div className="d-flex align-items-center gap-2">
                        <Form.Control
                            type="date"
                            size="sm"
                            style={{ width: 150 }}
                            value={customRange.from}
                            onChange={(e) => setCustomRange((prev) => ({ ...prev, from: e.target.value }))}
                        />
                        <span className="text-muted small">—</span>
                        <Form.Control
                            type="date"
                            size="sm"
                            style={{ width: 150 }}
                            value={customRange.to}
                            onChange={(e) => setCustomRange((prev) => ({ ...prev, to: e.target.value }))}
                        />
                    </div>
                )}
            </div>

            <div className="d-flex flex-wrap gap-4 align-items-end mb-3">
                <div>
                    <div className="text-muted small">
                        Поточний баланс {sizingMode === "percent" ? "(відсоток)" : "(фікс)"}
                    </div>
                    <div className="fs-4 fw-semibold">{currentBalance.toFixed(2)} USDT</div>
                </div>
                <div>
                    <div className="text-muted small">Зміна за період</div>
                    <div className={`fs-4 fw-semibold ${periodBalance.pnl > 0 ? "text-success" : periodBalance.pnl < 0 ? "text-danger" : ""}`}>
                        {periodBalance.pnl > 0 ? "+" : ""}{periodBalance.pnl.toFixed(2)} USDT
                    </div>
                </div>
                <div>
                    <div className="text-muted small">ROI за період</div>
                    <div className={`fs-4 fw-semibold ${roi == null ? "" : roi > 0 ? "text-success" : roi < 0 ? "text-danger" : ""}`}>
                        {roi == null ? "—" : `${roi > 0 ? "+" : ""}${roi}%`}
                    </div>
                </div>
                <div>
                    <div className="text-muted small">Сер. заробіток / день</div>
                    <div className={`fs-4 fw-semibold ${avgPnlPerDay > 0 ? "text-success" : avgPnlPerDay < 0 ? "text-danger" : ""}`}>
                        {avgPnlPerDay > 0 ? "+" : ""}{avgPnlPerDay.toFixed(2)} USDT
                    </div>
                </div>
                <div>
                    <div className="text-muted small" title="Binance Futures: вхід маркетом (taker 0.05%) + вихід лімітом (maker 0.02%) від margin×leverage">
                        Комісії за період
                    </div>
                    <div className="fs-4 fw-semibold text-danger">
                        −{periodFees.toFixed(2)} USDT
                    </div>
                </div>
                <div>
                    <div className="text-muted small">Тейк / Стоп</div>
                    <div className="fs-4 fw-semibold">
                        <span className="text-success">{tradeStats.wins}</span>
                        {" / "}
                        <span className="text-danger">{tradeStats.losses}</span>
                    </div>
                </div>
                <div>
                    <div className="text-muted small">Winrate</div>
                    <div className="fs-4 fw-semibold">
                        {tradeStats.winRate == null ? "—" : `${tradeStats.winRate}%`}
                    </div>
                </div>
                <div>
                    <div className="text-muted small">Удачні серії (довжина×кількість)</div>
                    <div className="fs-6 fw-semibold text-success">
                        {streakStats.winStreaks.length === 0
                            ? "—"
                            : streakStats.winStreaks.map(({ len, count }) => `${len}×${count}`).join("  ")}
                    </div>
                </div>
                <div>
                    <div className="text-muted small">Невдалі серії (довжина×кількість)</div>
                    <div className="fs-6 fw-semibold text-danger">
                        {streakStats.lossStreaks.length === 0
                            ? "—"
                            : streakStats.lossStreaks.map(({ len, count }) => `${len}×${count}`).join("  ")}
                    </div>
                </div>
            </div>

            <BalanceChart points={periodBalancePoints} />

            <WeeklyActivityChart data={weeklyActivity} />

            <div className="d-flex gap-2 mb-2">
                <Button
                    size="sm"
                    variant={pairSort.key === "open" ? "primary" : "outline-secondary"}
                    onClick={() => togglePairSort("open")}
                >
                    Відкриті спочатку
                </Button>
                <Button
                    size="sm"
                    variant={pairSort.key === "symbol" ? "primary" : "outline-secondary"}
                    onClick={() => togglePairSort("symbol")}
                >
                    А-Я {pairSort.key === "symbol" && (pairSort.dir === "asc" ? "↑" : "↓")}
                </Button>
                <Button
                    size="sm"
                    variant={pairSort.key === "winrate" ? "primary" : "outline-secondary"}
                    onClick={() => togglePairSort("winrate")}
                >
                    Winrate {pairSort.key === "winrate" && (pairSort.dir === "asc" ? "↑" : "↓")}
                </Button>
                <Button
                    size="sm"
                    variant={pairSort.key === "count" ? "primary" : "outline-secondary"}
                    onClick={() => togglePairSort("count")}
                >
                    К-сть угод {pairSort.key === "count" && (pairSort.dir === "asc" ? "↑" : "↓")}
                </Button>
                <Button
                    size="sm"
                    variant={pairSort.key === "earned" ? "primary" : "outline-secondary"}
                    onClick={() => togglePairSort("earned")}
                >
                    Заробіток {pairSort.key === "earned" && (pairSort.dir === "asc" ? "↑" : "↓")}
                </Button>
                {pairSort.key && (
                    <Button size="sm" variant="outline-secondary" onClick={() => setPairSort({ key: null, dir: "asc" })}>
                        Скинути
                    </Button>
                )}
                <Button
                    size="sm"
                    variant={groupByDay ? "primary" : "outline-secondary"}
                    onClick={() => setGroupByDay((prev) => !prev)}
                >
                    Групувати по днях
                </Button>
            </div>

            <div className="table-responsive">
                <table className="table align-middle">
                    {groupByDay && (
                        <colgroup>
                            <col />
                            {dayColumns.map((day) => (
                                <col key={day} style={{ width: dayColumnWidth(day) }} />
                            ))}
                            <col />
                        </colgroup>
                    )}
                    {groupByDay && (
                        // .table-light на thead — інакше глобальне table.table thead:not(.table-light)
                        // з layout/_common.scss дає суцільний темний фон + світлий текст на будь-якій
                        // шапці таблиці, і день-мітки читались би дуже важко.
                        <thead className="table-light">
                            <tr>
                                <th className="fw-normal" style={{ whiteSpace: "nowrap" }}>
                                    Пара
                                </th>
                                {dayColumns.map((day) => (
                                    <th
                                        key={day}
                                        className="text-muted small fw-normal text-center"
                                        style={{ whiteSpace: "nowrap", borderLeft: "1px solid #dee2e6" }}
                                    >
                                        {dayjs(day).format("D MMM")}
                                    </th>
                                ))}
                                <th />
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {sortedPairs.map((pair) => {
                            const pendingCount = pendingCountByPairId.get(pair._id) || 0;
                            const atLimit = pendingCount >= MAX_PENDING_BETS;
                            const winRate = pairWinRate(pair);
                            const earned = round2(pairEarnedMap.get(pair.symbol) || 0);

                            const renderChip = (bet) => (
                                <BetChip
                                    key={bet._id}
                                    bet={bet}
                                    onCycle={() => handleCycleBetStatus(bet)}
                                    onEdit={() => setEditBet(bet)}
                                    onDelete={() => handleDeleteBet(bet._id)}
                                    onScreenshots={() => setScreenshotsBetId(bet._id)}
                                    locked={locked}
                                    deleteLocked={deleteLocked}
                                />
                            );

                            const addBetButton = (
                                <OverlayTrigger
                                    overlay={
                                        atLimit ? (
                                            <Tooltip>Максимум {MAX_PENDING_BETS} відкриті ставки на пару</Tooltip>
                                        ) : (
                                            <Tooltip>Додати ставку</Tooltip>
                                        )
                                    }
                                >
                                    <span>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            disabled={atLimit}
                                            onClick={() => setAddBetPairId(pair._id)}
                                            style={{ width: 28, height: 28, padding: 0 }}
                                        >
                                            <Plus size={16} />
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                            );

                            const pairInfoCell = (
                                <td style={{ whiteSpace: "nowrap", width: 1 }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="fw-semibold">{pair.symbol}</span>
                                        <span className="text-muted small">
                                            {winRate == null ? "—" : `${winRate}%`} · {pair.bets.length}
                                        </span>
                                        <span
                                            className={`small fw-semibold ${earned > 0 ? "text-success" : earned < 0 ? "text-danger" : "text-muted"}`}
                                        >
                                            {earned > 0 ? "+" : ""}{earned.toFixed(2)}$
                                        </span>
                                        {!deleteLocked && (
                                            <Button
                                                variant="link"
                                                className="p-0 text-danger"
                                                onClick={() => setDeletePair(pair)}
                                                title="Видалити пару"
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            );

                            if (groupByDay) {
                                const betsByDay = pair.bets.reduce((acc, bet) => {
                                    const day = dayjs(bet.placedAt).format("YYYY-MM-DD");
                                    (acc[day] = acc[day] || []).push(bet);
                                    return acc;
                                }, {});
                                return (
                                    <tr key={pair._id}>
                                        {pairInfoCell}
                                        {dayColumns.map((day) => (
                                            <td key={day} style={{ borderLeft: "1px solid #dee2e6" }}>
                                                <div className="d-flex flex-row gap-2">
                                                    {[...(betsByDay[day] || [])].reverse().map(renderChip)}
                                                </div>
                                            </td>
                                        ))}
                                        <td>{addBetButton}</td>
                                    </tr>
                                );
                            }

                            return (
                                <tr key={pair._id}>
                                    {pairInfoCell}
                                    <td>
                                        <div className="d-flex align-items-center gap-2 flex-nowrap overflow-auto">
                                            {[...pair.bets].reverse().map(renderChip)}
                                            {addBetButton}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {pairs.length === 0 && (
                            <tr>
                                <td colSpan={2} className="text-muted">
                                    Пар ще немає — додайте першу вище.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <AddBetModal
                show={!!addBetPairId}
                onHide={() => setAddBetPairId(null)}
                onSubmit={handleAddBet}
                previousBet={previousBetForAdd}
                balance={realBalance}
                defaultAmount={baseAmount}
            />
            <EditBetModal
                bet={editBet}
                onHide={() => setEditBet(null)}
                onSubmit={handleEditBet}
                onDelete={handleDeleteBet}
                balance={realBalance}
            />
            <DeletePairModal pair={deletePair} onHide={() => setDeletePair(null)} onConfirm={handleDeletePair} />
            <ScreenshotsModal
                bet={screenshotsBet}
                onHide={() => setScreenshotsBetId(null)}
                onUpload={handleUploadScreenshot}
                onDelete={handleDeleteScreenshot}
            />
            <SettingsModal
                show={showSettings}
                onHide={() => setShowSettings(false)}
                depositInput={depositInput}
                setDepositInput={setDepositInput}
                savingDeposit={savingDeposit}
                onSaveDeposit={handleSaveDeposit}
                depositError={depositError}
                baseAmountInput={baseAmountInput}
                setBaseAmountInput={setBaseAmountInput}
                savingBaseAmount={savingBaseAmount}
                onSaveBaseAmount={handleSaveBaseAmount}
                baseAmountError={baseAmountError}
                baseAmount={baseAmount}
                sizingMode={sizingMode}
                setSizingMode={setSizingMode}
                locked={locked}
                setLocked={setLocked}
                deleteLocked={deleteLocked}
                setDeleteLocked={setDeleteLocked}
                newSymbol={newSymbol}
                setNewSymbol={setNewSymbol}
                addingPair={addingPair}
                pairError={pairError}
                onAddPair={handleAddPair}
            />
        </div>
    );
};

export default TradingPage;
